import type { Config, Font, Layout } from "plotly.js"

/**
 * Reprend la structure visuelle de `homeys_kit/graph/constants/template_graph.json`
 * (clé `light.layout`). L’axe X des profils seuils utilise des indices (0…n) : on
 * applique le style du template en `type: "linear"` ; pour une série temporelle,
 * surchargez via `chartOptions` (ex. `xaxis.type: "date"`, `tickformat: "%d/%m/%Y"`).
 *
 * Référence : `homeys-kit/homeys_kit/graph/constants/template_graph.json`
 */
const TICK_FONT: Partial<Font> = {
    family: "Arial",
    size: 10,
    color: "#2D2E32",
}

const AXIS_TITLE_FONT: Partial<Font> = {
    family: "Arial",
    size: 12,
    color: "#2D2E32",
}

export const ALARM_THRESHOLD_PLOTLY_TEMPLATE_LAYOUT: Partial<Layout> = {
    paper_bgcolor: "#fff",
    plot_bgcolor: "#fff",
    margin: { l: 40, r: 40, t: 0, b: 0 },
    colorway: [
        "#35C4DC",
        "#6586D5",
        "#EAB308",
        "#EC4899",
        "#22C55E",
        "#6366F1",
        "#F97316",
        "#64748B",
        "#14B8A6",
        "#FF3D32",
        "#A855F7",
        "#9E9E9E",
    ],
    hovermode: "x unified",
    hoverlabel: {
        bgcolor: "#FFFFFF",
        bordercolor: "#ACAEB2",
        font: {
            family: "Arial",
            size: 12,
            color: "#000000",
        },
    },
    showlegend: true,
    xaxis: {
        type: "linear",
        tickfont: TICK_FONT,
        title: {
            text: "",
            font: AXIS_TITLE_FONT,
        },
        linecolor: "#ACAEB2",
        tickcolor: "#ECECEC",
        tickwidth: 1,
        ticklen: 10,
        ticks: "outside",
        rangeslider: {
            visible: true,
            bgcolor: "#FFFFFF",
            bordercolor: "#ECECEC",
            borderwidth: 1,
            thickness: 0.15,
        },
    },
    yaxis: {
        tickfont: TICK_FONT,
        title: {
            font: AXIS_TITLE_FONT,
            standoff: 25,
        },
        linecolor: "#ACAEB2",
        gridcolor: "#ECECEC",
        tickcolor: "#ECECEC",
        tickwidth: 1,
        ticklen: 10,
        ticks: "outside",
    },
    legend: {
        orientation: "h",
        x: 0.05,
        y: 0,
        xanchor: "left",
        yanchor: "bottom",
        xref: "container",
        yref: "container",
        bgcolor: "#fff",
        font: {
            family: "Arial",
            size: 10,
            color: "#2D2E32",
        },
        groupclick: "toggleitem",
    },
}

export const ALARM_THRESHOLD_PLOTLY_TEMPLATE_CONFIG: Partial<Config> = {
    responsive: true,
    displaylogo: false,
}

/**
 * Fusion superficielle des clés de layout, avec fusion des sous-objets `xaxis`, `yaxis`,
 * `legend`, `margin`, `hoverlabel`.
 *
 * @param overrides options passées par le parent (AlarmPlotlyChart)
 * @returns layout prêt pour Plotly.newPlot
 */
export function mergeAlarmThresholdPlotlyLayout(
    overrides?: Partial<Layout>
): Partial<Layout> {
    const base = ALARM_THRESHOLD_PLOTLY_TEMPLATE_LAYOUT
    if (!overrides) {
        return { ...base }
    }

    return {
        ...base,
        ...overrides,
        margin: { ...base.margin, ...overrides.margin },
        hoverlabel: {
            ...base.hoverlabel,
            ...overrides.hoverlabel,
            font: {
                ...base.hoverlabel?.font,
                ...overrides.hoverlabel?.font,
            },
        },
        xaxis: {
            ...base.xaxis,
            ...overrides.xaxis,
            title: {
                ...base.xaxis?.title,
                ...overrides.xaxis?.title,
                font: {
                    ...base.xaxis?.title?.font,
                    ...overrides.xaxis?.title?.font,
                },
            },
            rangeslider: {
                ...base.xaxis?.rangeslider,
                ...overrides.xaxis?.rangeslider,
            },
            tickfont: { ...base.xaxis?.tickfont, ...overrides.xaxis?.tickfont },
        },
        yaxis: {
            ...base.yaxis,
            ...overrides.yaxis,
            title: {
                ...base.yaxis?.title,
                ...overrides.yaxis?.title,
                font: {
                    ...base.yaxis?.title?.font,
                    ...overrides.yaxis?.title?.font,
                },
            },
            tickfont: { ...base.yaxis?.tickfont, ...overrides.yaxis?.tickfont },
        },
        legend: {
            ...base.legend,
            ...overrides.legend,
            font: { ...base.legend?.font, ...overrides.legend?.font },
        },
    }
}
